import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { OnboardingFlow } from '../../../../../../backend/src/user/onboarding-flow';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteMatter } from '@/api/mutations/useDeleteMatter';
import { Matter } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { CoachMark } from '@/new/components/coach-mark';
import { Icon } from '@/new/components/icon';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

interface DraftMattersDialogProps {
  matters?: Matter[];
}

export function DraftMattersDialog({ matters }: DraftMattersDialogProps) {
  const { user } = useUserContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const scrollElRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = useCallback(() => {
    const el = scrollElRef.current;
    if (!el) return;
    setIsScrolledToBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 5);
  }, []);

  const scrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollElRef.current = node;
      handleScroll();
    },
    [handleScroll],
  );

  const draftMatters = useMemo(
    () => matters?.filter((matter) => matter.isDraft && matter.createdById === user?.id) ?? [],
    [matters, user?.id],
  );

  const filteredDraftMatters = draftMatters.filter((matter) =>
    (matter.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()),
  );

  useEffect(() => {
    handleScroll();
  }, [draftMatters.length, handleScroll]);

  if (draftMatters.length === 0) return null;

  return (
    <Dialog>
      <CoachMark
        flowId={OnboardingFlow.DraftMattersCoachMark}
        show={draftMatters.length === 1}
        side="left"
        align="center"
        content="Matters that you haven't finished creating can be completed from here."
      >
        <DialogTrigger asChild>
          <Button data-testid="drafts-btn">
            <Icon className="lucide--circle-dashed mr-0.5 text-xs" />
            <span>
              Finish creating draft Matters <span className="font-normal text-gray-400">({draftMatters.length})</span>
            </span>
          </Button>
        </DialogTrigger>
      </CoachMark>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <Icon className="lucide--circle-dashed text-slate-600" />
              <span>
                Draft Matters <span className="font-normal text-gray-500">({draftMatters.length})</span>
              </span>
            </div>
          </DialogTitle>
          <DialogDescription>Continue where you left off to finish creating these matters.</DialogDescription>
        </DialogHeader>

        <div className="-m-2 -mx-3">
          <SearchInput
            placeholder={`Search ${filteredDraftMatters.length} draft ${filteredDraftMatters.length === 1 ? 'matter' : 'matters'}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex h-[50vh] flex-col overflow-y-auto mask-b-from-65% mask-b-to-100% px-1 pt-2 transition-[mask-size] duration-200"
            style={{ maskSize: isScrolledToBottom ? '100% 135%' : '100% 100%' }}
          >
            {filteredDraftMatters.length === 0 && (
              <p className="my-12 text-center text-gray-400">
                {searchQuery ? 'No matching drafts.' : 'No draft matters yet.'}
              </p>
            )}

            {filteredDraftMatters.map((matter) => (
              <DraftMatterListItem key={matter.id} matter={matter} />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DraftMatterListItem({ matter }: { matter: Matter }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const { mutate: deleteMatter, isLoading: isDeletingMatter } = useDeleteMatter();

  return (
    <div className="group focus:outline-none">
      <div
        onClick={() => {
          trackCustomEvent(TrackingEventsV2.NavigationClickMatterCard, {
            matterId: matter.id,
          });
          navigate(`/matters/new/${matter.id}`);
        }}
        className={cn(
          'flex cursor-pointer items-center justify-between gap-3 rounded-md p-3 ring-teal-500 ring-offset-2 transition-colors duration-150 group-focus-visible:ring-1 hover:bg-gray-50',
          isDeletingMatter ? 'opacity-50' : 'opacity-100',
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className={cn('truncate font-medium', matter.name ? 'text-gray-700' : 'text-gray-500')}>
            {matter.name || 'Untitled Matter'}
          </span>
          {matter.code && <span className="flex-shrink-0 truncate font-mono text-gray-500">{matter.code}</span>}
        </div>

        <div className="flex flex-shrink-0 items-center gap-4">
          <p className="text-xs text-gray-400">
            Created{' '}
            {new Date(matter.createdDate).toLocaleString(i18n.language, {
              dateStyle: 'medium',
              timeStyle: 'medium',
            })}
          </p>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                onClick={(e) => e.preventDefault()}
                disabled={isDeletingMatter}
                data-testid="matter-card-ellipsis-menu"
              >
                <Icon className="lucide--ellipsis-vertical" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {matter.canDelete ? (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteMatter(matter.id);
                  }}
                  disabled={isDeletingMatter}
                  className="text-red-600 focus:bg-red-50 focus:text-red-700"
                >
                  <Icon className="lucide--trash-2 h-4 w-4" />
                  Delete matter
                </DropdownMenuItem>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      disabled
                      className="cursor-not-allowed text-gray-400"
                      onClick={(e) => e.preventDefault()}
                    >
                      <Icon className="lucide--trash-2 h-4 w-4" />
                      Delete Matter
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xl" sideOffset={16} avoidCollisions={false}>
                    <p>You don't have permission to delete this Matter. Ask a Matter admin or an org administrator.</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <hr className="mx-1 my-px group-last:opacity-0" />
    </div>
  );
}
